import { useId, useMemo, useState, type FormEvent } from 'react';
import { useLacTenantsQuery } from '../../features/lac/queries/useLacTenantsQuery';
import { useI18n } from '../../i18n/useI18n';
import type { LacCreateUserInput } from '../../types/lac';
import { isValidEmailFormat, containsWhitespace } from '../../utils/formUtils';
import { dateInputToUnixMillis } from '../../utils/dateTimeUtils';
import {
  DEFAULT_LAC_CLIENT_ROLE,
  isLacClientRole,
  LAC_ASSIGNABLE_ROLES,
  type LacClientRole,
} from './lacRoles';

export const CREATE_NEW_TENANT_VALUE = '__create_new_tenant__';

export type UseCreateLacUserDialogFormArgs = {
  onCreate: (input: LacCreateUserInput) => void;
  isSubmitting: boolean;
  fixedTenantName?: string;
  fixedTenantDisplayName?: string;
};

export function useCreateLacUserDialogForm({
  onCreate,
  isSubmitting,
  fixedTenantName,
  fixedTenantDisplayName,
}: UseCreateLacUserDialogFormArgs) {
  const { translate } = useI18n();
  const tenantFieldId = useId();
  const roleFieldId = useId();
  const sendInviteEmailFieldId = useId();
  const optionalFieldsId = useId();

  const isTenantFixed = Boolean(fixedTenantName?.trim());
  const tenantsQuery = useLacTenantsQuery();
  const tenants = useMemo(
    () => (isTenantFixed ? [] : (tenantsQuery.data ?? [])),
    [isTenantFixed, tenantsQuery.data],
  );

  const [tenantSelection, setTenantSelection] = useState('');
  const [newTenantName, setNewTenantName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState<LacClientRole>(DEFAULT_LAC_CLIENT_ROLE);
  const [sendInviteEmail, setSendInviteEmail] = useState(true);
  const [validFrom, setValidFrom] = useState('');
  const [validTo, setValidTo] = useState('');
  const [usernameTouched, setUsernameTouched] = useState(false);
  const [showOptionalFields, setShowOptionalFields] = useState(false);

  const isCreatingNewTenant =
    !isTenantFixed &&
    (tenants.length === 0 || tenantSelection === CREATE_NEW_TENANT_VALUE);

  const selectedExistingTenantName =
    !isCreatingNewTenant &&
    tenantSelection !== '' &&
    tenants.some((tenant) => tenant.name === tenantSelection)
      ? tenantSelection
      : (tenants[0]?.name ?? '');

  const tenantSelectValue = isCreatingNewTenant
    ? CREATE_NEW_TENANT_VALUE
    : selectedExistingTenantName;

  const resolvedTenantName = isTenantFixed
    ? (fixedTenantName?.trim() ?? '')
    : isCreatingNewTenant
      ? newTenantName.trim()
      : selectedExistingTenantName.trim();

  const isDuplicateTenantName =
    isCreatingNewTenant &&
    tenants.some((tenant) => tenant.name === resolvedTenantName);

  const trimmedEmail = email.trim();
  const isEmailInvalid =
    trimmedEmail !== '' && !isValidEmailFormat(trimmedEmail);

  const trimmedUsername = username.trim();
  const isUsernameInvalid =
    trimmedUsername !== '' && containsWhitespace(username);

  const validFromMillis = dateInputToUnixMillis(validFrom);
  const validToMillis = dateInputToUnixMillis(validTo);
  const isValidToBeforeValidFrom =
    validFromMillis != null &&
    validToMillis != null &&
    validToMillis < validFromMillis;

  const isCreateDisabled =
    isSubmitting ||
    resolvedTenantName === '' ||
    isDuplicateTenantName ||
    trimmedEmail === '' ||
    isEmailInvalid ||
    trimmedUsername === '' ||
    isUsernameInvalid ||
    isValidToBeforeValidFrom ||
    firstName.trim() === '' ||
    lastName.trim() === '' ||
    (!isTenantFixed && (tenantsQuery.isLoading || tenantsQuery.isError));

  const handleTenantChange = (value: string) => {
    setTenantSelection(value);
    if (value !== CREATE_NEW_TENANT_VALUE) {
      setNewTenantName('');
    }
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (!usernameTouched) {
      setUsername(value.trim());
    }
  };

  const handleUsernameChange = (value: string) => {
    setUsernameTouched(true);
    setUsername(value);
  };

  const handleRoleChange = (value: string) => {
    if (isLacClientRole(value)) {
      setRole(value);
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isCreateDisabled) {
      return;
    }

    onCreate({
      tenantName: resolvedTenantName,
      createTenant: isTenantFixed ? false : isCreatingNewTenant,
      username: trimmedUsername,
      email: trimmedEmail,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      role,
      sendInviteEmail,
      validFrom: validFrom.trim() || undefined,
      validTo: validTo.trim() || undefined,
    });
  };

  const tenantOptions = [
    ...tenants.map((tenant) => ({
      value: tenant.name,
      label: tenant.displayName?.trim() || tenant.name,
    })),
    {
      value: CREATE_NEW_TENANT_VALUE,
      label: translate('lac.users.create.organisationCreateNew'),
    },
  ];

  const roleOptions = LAC_ASSIGNABLE_ROLES.map((value) => ({
    value,
    label: translate(`lac.users.roles.${value}`),
  }));

  const fixedTenantDisplayValue =
    fixedTenantDisplayName?.trim() || fixedTenantName?.trim() || '';

  return {
    translate,
    tenantFieldId,
    roleFieldId,
    sendInviteEmailFieldId,
    optionalFieldsId,
    isTenantFixed,
    tenants,
    tenantsQuery,
    newTenantName,
    setNewTenantName,
    username,
    email,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    role,
    sendInviteEmail,
    setSendInviteEmail,
    validFrom,
    setValidFrom,
    validTo,
    setValidTo,
    showOptionalFields,
    setShowOptionalFields,
    isCreatingNewTenant,
    tenantSelectValue,
    isDuplicateTenantName,
    isEmailInvalid,
    isUsernameInvalid,
    isValidToBeforeValidFrom,
    isCreateDisabled,
    handleTenantChange,
    handleEmailChange,
    handleUsernameChange,
    handleRoleChange,
    handleSubmit,
    tenantOptions,
    roleOptions,
    fixedTenantDisplayValue,
  };
}
